import {
  ApiTags,
  BillingAgreementDetails,
  GetNewPaymentPriceResponse,
  NewPaymentPriceResponse,
  GetPaymentLinkRequest,
  GroupData,
  PaymentDetails,
  PaymentLinkStatus,
  PaymentMethod,
  PlanDetails,
  SubscriptionType,
  ApiGeneralDataResponse,
  ApiResponse,
  PlanUsageLimits,
  DisplayCurrencyDataPoint,
  GetUsageLimitsResponse,
  ApprovePaymentRequest,
  PaymentLinkData,
  CancellationReasonOption,
  SaveCancellationReasonRequest,
  ConnectEmailRequest,
  ConnectEmailResponse,
  ConfirmEmailConnectionRequest,
  ApproveSubscriptionRequest,
  CheckExperienceConnectionRequestResponse,
  ExperienceConnectionStatus,
  SubGroupType,
  AssetManagerConfigurationType,
  AssetManagerInvoice,
  generateAssetManagerInvoiceRequest,
  CreatePayoutMethodRequest,
  EditPayoutMethodRequest,
  PayoutMethodSource,
  PayoutMethod,
  updateAssetManagerInvoiceStatus,
  updateAssetManagerInvoiceVisibility,
  SetAssetManagerConfigurationRequest,
  GetUpgradePricesResponse,
  GetUpgradePricesRequest,
} from "@shared/types";
import { EndpointBuilder } from "@reduxjs/toolkit/query";
import moment from "moment";
import {
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_MONTHS,
} from "@shared/common/constants";
import { getActiveGroups } from "@shared/features/settings/utils";
import { getSignalsFiltersSlice } from "@shared/features/signals/common/utils";
import { addInviteTokenAuthHeader } from "@shared/utils/misc";

export const settingsEndpoints = {
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    getAllPlanDetails: build.query<
      PlanDetails[],
      { sub_group_id?: number; promo_code?: string | null } | void
    >({
      query: (args) => ({
        url: "get_all_plan_details/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: { plans: PlanDetails[] } }) =>
        response.data.plans,
    }),
    getAllPaymentMethods: build.query<
      PaymentMethod[],
      { sub_group_id: number } | void
    >({
      query: (args) => ({
        url: "get_all_payment_methods/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: {
        data: { payment_options: PaymentMethod[] };
      }) => response.data.payment_options,
    }),
    getPaymentLink: build.mutation<PaymentLinkData, GetPaymentLinkRequest>({
      query: (args) => ({
        url: "get_payment_link/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: PaymentLinkData }) => response.data,
    }),
    getPaymentStatus: build.mutation<PaymentLinkStatus, { invoice_id: string }>(
      {
        query: (args) => ({
          url: "get_payment_status/",
          method: "POST",
          body: args,
        }),
        transformResponse: (response: { data: PaymentLinkStatus }) =>
          response.data,
      }
    ),
    getRecentPayments: build.query<
      PaymentDetails[],
      { subscription_type: SubscriptionType }
    >({
      query: (args) => ({
        url: "get_recent_payments/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: {
        data: { recent_payments: PaymentDetails[] };
      }) => response.data.recent_payments,
      providesTags: () => [{ type: ApiTags.RECENT_PAYMENTS, id: "LIST" }],
    }),
    cancelSubscription: build.mutation<void, { sub_group_id: number } | void>({
      query: (args) => ({
        url: "cancel_subscription/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response, _, args) => {
        let tags = [{ type: ApiTags.RECENT_PAYMENTS, id: "LIST" }];
        if (args?.sub_group_id) {
          tags.push({ type: ApiTags.GROUPS, id: "LIST" });
        } else {
          tags.push({ type: ApiTags.USER, id: "LIST" });
        }
        return response ? tags : [];
      },
    }),
    getCancellationReasons: build.query<CancellationReasonOption[], void>({
      query: (args) => ({
        url: "get_cancellation_reasons/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<{ reasons: CancellationReasonOption[] }>
      ) => response.data.reasons,
    }),
    saveCancellationReason: build.mutation<void, SaveCancellationReasonRequest>(
      {
        query: (args) => ({
          url: "save_cancellation_reason/",
          method: "POST",
          body: args,
        }),
      }
    ),
    getCancelSubscriptionPromoCode: build.query<string, void>({
      query: (args) => ({
        url: "get_cancel_subscription_promo_code/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<{ promo_code: string }>) =>
        response.data.promo_code,
    }),
    getBillingAgreementDetails: build.query<
      BillingAgreementDetails,
      { payment_token: string }
    >({
      query: (args) => ({
        url: "get_billing_agreement_details/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: BillingAgreementDetails }) =>
        response.data,
    }),
    getGroupsData: build.query<GroupData[], void>({
      query: (args) => ({
        url: "get_groups_data/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data: { data: GroupData[] } }) =>
        response.data.data,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const openSignalsFilters = getSignalsFiltersSlice(true);
          const closedSignalsFilters = getSignalsFiltersSlice(false);
          const filteredData = getActiveGroups(data);
          dispatch(
            openSignalsFilters.actions.loadGroups({ data: filteredData || [] })
          );
          dispatch(
            closedSignalsFilters.actions.loadGroups({
              data: filteredData || [],
            })
          );
        } catch {}
      },
      providesTags: () => [{ type: ApiTags.GROUPS, id: "LIST" }],
    }),
    getNewPaymentPrice: build.query<
      NewPaymentPriceResponse,
      Omit<GetPaymentLinkRequest, "email">
    >({
      query: (args) => ({
        url: "get_new_payment_price/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<GetNewPaymentPriceResponse>
      ) => ({
        ...response.data,
        message: response.message,
        code: response.code,
      }),
    }),
    getAllQuotaUsage: build.query<PlanUsageLimits, void>({
      query: () => ({
        url: "get_all_quota_usage/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<PlanUsageLimits>>
      ) => response.data.data,
      providesTags: [{ type: ApiTags.QUOTA_USAGE, id: "LIST" }],
    }),
    getUsageLimits: build.query<
      GetUsageLimitsResponse,
      { is_demo_query?: boolean } | void
    >({
      query: (args) => ({
        url: "get_usage_limits/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<GetUsageLimitsResponse>>
      ) => {
        return response.data.data;
      },
    }),
    getTradeUsageChart: build.query<DisplayCurrencyDataPoint[], void>({
      query: () => ({
        url: "get_trade_usage_chart/",
        method: "POST",
        body: {
          time_range: TIME_RANGE_1_MONTHS,
          time_aggregation: TIME_RANGE_1_DAY,
          timezone_difference: moment().utcOffset(),
        },
      }),
      transformResponse: (
        response: ApiResponse<
          ApiGeneralDataResponse<DisplayCurrencyDataPoint[]>
        >
      ) => {
        return response.data.data;
      },
    }),
    approvePayment: build.mutation<void, ApprovePaymentRequest>({
      query: (args) => ({
        url: "approve_payment/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response
          ? [
              { type: ApiTags.USER, id: "LIST" },
              { type: ApiTags.RECENT_PAYMENTS, id: "LIST" },
            ]
          : [],
    }),
    approveSubscription: build.mutation<void, ApproveSubscriptionRequest>({
      query: (args) => ({
        url: "approve_subscription/",
        method: "POST",
        body: args,
      }),
    }),
    disconnectTelegramAccount: build.mutation<void, void>({
      query: () => ({
        url: "disconnect_telegram_account_confirmation_email/",
        method: "POST",
      }),
    }),
    disconnectDiscordAccount: build.mutation<void, void>({
      query: () => ({
        url: "disconnect_discord_account_confirmation_email/",
        method: "POST",
      }),
    }),
    getCurrentDiscount: build.query<number | undefined, void>({
      query: (args) => ({
        url: "get_current_discount/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: { data?: { discount: number } }) =>
        response.data?.discount,
    }),
    connectEmail: build.mutation<ConnectEmailResponse, ConnectEmailRequest>({
      query: (args) => ({
        url: "connect_email/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiGeneralDataResponse<ConnectEmailResponse>
      ) => response.data,
    }),
    confirmEmailConnection: build.mutation<void, ConfirmEmailConnectionRequest>(
      {
        query: (args) => ({
          url: "confirm_email_connection/",
          method: "POST",
          body: args,
        }),
        invalidatesTags: (response) => {
          return response ? [{ type: ApiTags.USER, id: "LIST" }] : [];
        },
      }
    ),
    createSubGroup: build.mutation<
      { sub_group_id: number; name: string },
      { name: string; sub_group_type: SubGroupType }
    >({
      query: (args) => ({
        url: "create_sub_group/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) => {
        return response
          ? [
              { type: ApiTags.GROUPS, id: "LIST" },
              { type: ApiTags.ADMIN_SUB_GROUPS, id: "LIST" },
            ]
          : [];
      },
      transformResponse: (
        response: ApiGeneralDataResponse<{ sub_group_id: number; name: string }>
      ) => response.data,
    }),
    editSubGroup: build.mutation<void, { sub_group_id: number; name: string }>({
      query: (args) => ({
        url: "edit_sub_group/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) => {
        return response
          ? [
              { type: ApiTags.GROUPS, id: "LIST" },
              { type: ApiTags.ADMIN_SUB_GROUPS, id: "LIST" },
            ]
          : [];
      },
    }),
    deleteSubGroup: build.mutation<void, { sub_group_id: number }>({
      query: (args) => ({
        url: "delete_sub_group/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) => {
        return response
          ? [
              { type: ApiTags.GROUPS, id: "LIST" },
              { type: ApiTags.ADMIN_SUB_GROUPS, id: "LIST" },
            ]
          : [];
      },
    }),
    generateExperienceConnectionRequest: build.mutation<string, void>({
      query: (args) => ({
        url: "generate_experience_connection_request/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<{ code: string }>) =>
        response.data.code,
    }),
    checkExperienceConnectionRequest: build.query<
      CheckExperienceConnectionRequestResponse,
      { code: string }
    >({
      query: (args) => ({
        url: "check_experience_connection_request/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<CheckExperienceConnectionRequestResponse>
      ) => response.data,
    }),
    changeExperienceConnectionStatus: build.mutation<
      void,
      { status: ExperienceConnectionStatus; sub_group_id: number }
    >({
      query: (args) => ({
        url: "change_experience_connection_status/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response
          ? [
              { type: ApiTags.GROUPS, id: "LIST" },
              { type: ApiTags.ADMIN_SUB_GROUPS, id: "LIST" },
            ]
          : [],
    }),
    getAssetManagerConfiguration: build.query<
      AssetManagerConfigurationType,
      void
    >({
      query: (args) => ({
        url: "get_asset_manager_configuration/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<AssetManagerConfigurationType>
      ) => response.data,
    }),
    getWhitelabelConfiguration: build.query<
      AssetManagerConfigurationType,
      { authToken?: string } | void
    >({
      query: (args) => {
        return addInviteTokenAuthHeader(
          {
            url: "get_whitelabel_configuration/",
            method: "GET",
          },
          args
        );
      },
      transformResponse: (
        response: ApiResponse<AssetManagerConfigurationType>
      ) => response.data,
      keepUnusedDataFor: 0,
      providesTags: () => [
        { type: ApiTags.WHITELABEL_CONFIGURATION, id: "LIST" },
      ],
    }),
    getAssetManagerInvoices: build.query<AssetManagerInvoice[], void>({
      query: (args) => ({
        url: "get_asset_manager_invoices/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<AssetManagerInvoice[]>) =>
        response.data,
      providesTags: () => [{ type: ApiTags.INVOICES, id: "LIST" }],
    }),
    generateAssetManagerInvoice: build.mutation<
      AssetManagerInvoice,
      generateAssetManagerInvoiceRequest
    >({
      query: (args) => ({
        url: "generate_asset_manager_invoice/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<AssetManagerInvoice>) =>
        response.data,
      invalidatesTags: (response) =>
        response ? [{ type: ApiTags.INVOICES, id: "LIST" }] : [],
    }),
    updateAssetManagerInvoiceStatus: build.mutation<
      void,
      updateAssetManagerInvoiceStatus
    >({
      query: (args) => ({
        url: "update_asset_manager_invoice_status/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response ? [{ type: ApiTags.INVOICES, id: "LIST" }] : [],
    }),
    updateAssetManagerInvoiceVisibility: build.mutation<
      ApiResponse<AssetManagerInvoice>,
      updateAssetManagerInvoiceVisibility
    >({
      query: (args) => ({
        url: "update_asset_manager_invoice_visibility/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response ? [{ type: ApiTags.INVOICES, id: "LIST" }] : [],
    }),
    createPayoutMethod: build.mutation<void, CreatePayoutMethodRequest>({
      query: (args) => ({
        url: "create_payout_method/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response ? [{ type: ApiTags.PAYOUT_METHODS, id: "LIST" }] : [],
    }),
    editPayoutMethod: build.mutation<void, EditPayoutMethodRequest>({
      query: (args) => ({
        url: "edit_payout_method/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: (response) =>
        response ? [{ type: ApiTags.PAYOUT_METHODS, id: "LIST" }] : [],
    }),
    getPayoutMethods: build.query<
      PayoutMethod[],
      { source: PayoutMethodSource }
    >({
      query: (args) => ({
        url: "get_payout_methods/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<PayoutMethod[]>>
      ) => {
        return response.data.data;
      },
      providesTags: () => [{ type: ApiTags.PAYOUT_METHODS, id: "LIST" }],
    }),
    getUpgradePrices: build.query<
      GetUpgradePricesResponse,
      GetUpgradePricesRequest
    >({
      query: (args) => ({
        url: "get_upgrade_prices/",
        method: "POST",
        body: args,
      }),
      transformResponse: (response: ApiResponse<GetUpgradePricesResponse>) =>
        response.data,
    }),
    requestAssetManagerDemo: build.mutation<
      void,
      RequestAssetManagerDemoRequest
    >({
      query: (args) => ({
        url: "request_asset_manager_demo/",
        method: "POST",
        body: args,
      }),
    }),
    setAssetManagerConfiguration: build.mutation<
      AssetManagerConfigurationType,
      SetAssetManagerConfigurationRequest
    >({
      query: (args) => ({
        url: "set_asset_manager_configuration/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<AssetManagerConfigurationType>
      ) => {
        return response.data;
      },
      invalidatesTags: (response) =>
        response
          ? [{ type: ApiTags.WHITELABEL_CONFIGURATION, id: "LIST" }]
          : [],
    }),
  }),
};

interface RequestAssetManagerDemoRequest {
  first_name: string;
  last_name: string;
  email: string;
  number_of_clients: string;
  estimated_aum?: string;
  goals_and_challenges: string;
}
